import type { ReactNode } from "react";
import { MenuBar } from "./MenuBar";
import "./index.css";

const SOCIALS = [
    {
        label: "LinkedIn",
        href: "https://www.linkedin.com/in/cheryl-lane-6b538265/",
        icon: (
            <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
            </svg>
        ),
    },
    {
        label: "GitHub",
        href: "https://github.com/cheryllane",
        icon: (
            <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 .5C5.37.5 0 5.78 0 12.29c0 5.21 3.44 9.63 8.21 11.19.6.11.82-.25.82-.56 0-.28-.01-1.02-.02-2-3.34.71-4.04-1.58-4.04-1.58-.55-1.37-1.34-1.74-1.34-1.74-1.09-.73.08-.72.08-.72 1.21.08 1.84 1.22 1.84 1.22 1.07 1.8 2.81 1.28 3.5.98.11-.76.42-1.28.76-1.57-2.67-.3-5.47-1.31-5.47-5.81 0-1.28.47-2.33 1.23-3.15-.12-.3-.53-1.51.12-3.15 0 0 1.01-.32 3.3 1.2a11.6 11.6 0 0 1 6 0c2.29-1.52 3.3-1.2 3.3-1.2.65 1.64.24 2.85.12 3.15.77.82 1.23 1.87 1.23 3.15 0 4.51-2.81 5.5-5.49 5.79.43.36.81 1.08.81 2.18 0 1.58-.01 2.85-.01 3.24 0 .31.21.68.83.56A12.04 12.04 0 0 0 24 12.29C24 5.78 18.63.5 12 .5z" />
            </svg>
        ),
    },
];

const SKILLS = [
    "TypeScript",
    "React",
    "Express",
    "Bun",
    "Java",
    "Spring",
    "AI Coding",
    "Python",
    "Docker",
    "AWS",
    "CI/CD",
];

const PROJECTS = [
    {
        title: "",
        description: "",
        tags: ["React", "TypeScript", "Express"],
        href: "#",
    },
    {
        title: "",
        description: "",
        tags: ["Python", "CLI", "Cloud"],
        href: "#",
    },
    {
        title: "Pixel Garden",
        description: "",
        tags: ["AWS", "Lambda", "CI/CD"],
        href: "#",
    },
];

function ArrowIcon() {
    return (
        <span className="text-accent transition-transform duration-200 group-hover:translate-x-1">
            &rarr;
        </span>
    );
}

function SectionHeading({
    eyebrow,
    title,
    description,
}: {
    eyebrow: string;
    title: ReactNode;
    description?: string;
}) {
    return (
        <div className="mb-12 max-w-2xl">
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-accent">
                {eyebrow}
            </p>
            <h2 className="font-serif text-3xl font-medium tracking-tight sm:text-4xl">
                {title}
            </h2>
            {description && (
                <p className="mt-4 leading-relaxed text-muted">{description}</p>
            )}
        </div>
    );
}

function Hero() {
    return (
        <section
            id="home"
            className="mx-auto max-w-5xl px-6 pb-20 pt-36 sm:pt-44"
        >
            <div className="max-w-3xl">
                <h1 className="font-serif text-4xl font-medium leading-[1.1] tracking-tight sm:text-6xl">
                    I build software for work &mdash; and for the{" "}
                    <span className="font-script font-bold text-accent">
                        fun
                    </span>{" "}
                    of it.
                </h1>

                <p className="mt-7 max-w-2xl text-lg leading-relaxed text-muted">
                    Hi, I&apos;m Cheryl Lane, a software engineer who genuinely
                    enjoys the craft. By day I ship production websites and
                    internal tooling; the rest of the time I build little tools,
                    and side projects for myself.
                </p>

                <div className="mt-9 flex items-center gap-5">
                    {SOCIALS.map((s) => (
                        <a
                            key={s.label}
                            href={s.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={s.label}
                            className="text-muted transition-colors hover:text-accent"
                        >
                            {s.icon}
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}

function About() {
    return (
        <section id="about" className="mx-auto max-w-5xl px-6 py-20">
            <SectionHeading
                eyebrow="About"
                title={
                    <>
                        A bit about{" "}
                        <span className="font-script font-bold text-accent">
                            me
                        </span>
                    </>
                }
            />
            <div className="grid gap-10 md:grid-cols-12">
                <div className="space-y-4 text-lg leading-relaxed text-muted md:col-span-8">
                    <p>
                        I&apos;m a software engineer who loves the craft of
                        building things. I work across the stack, from database
                        schemas to polished interfaces, and I&apos;m happiest
                        turning a fuzzy idea into something real and useful.
                    </p>
                    <p>
                        For me, technology isn&apos;t just a job. Half the fun
                        is building small tools and side projects to scratch my
                        own itch. When I&apos;m not shipping at work (on complex
                        brownfield projects), you&apos;ll find me tinkering with
                        a new language, playing games, or lost in a good book.
                    </p>
                </div>
                <div className="md:col-span-4">
                    <p className="text-sm font-medium text-fg">Toolbox</p>
                    <p className="mt-3 leading-relaxed text-muted">
                        {SKILLS.join(" · ")}
                    </p>
                </div>
            </div>
        </section>
    );
}

function Projects() {
    return (
        <section id="projects" className="mx-auto max-w-5xl px-6 py-20">
            <SectionHeading
                eyebrow="Work"
                title="Selected projects"
                description="A mix of work and personal projects I've built along the way."
            />
            <div className="border-t border-line">
                {PROJECTS.map((project) => (
                    <a
                        key={project.title}
                        href={project.href}
                        className="group grid gap-2 border-b border-line py-7 sm:grid-cols-12 sm:gap-6"
                    >
                        <h3 className="font-serif text-xl font-medium transition-colors group-hover:text-accent sm:col-span-5">
                            {project.title}
                        </h3>
                        <div className="sm:col-span-5">
                            <p className="leading-relaxed text-muted">
                                {project.description}
                            </p>
                            <p className="mt-2 text-sm text-muted">
                                {project.tags.join(" · ")}
                            </p>
                        </div>
                        <div className="hidden items-start justify-end sm:col-span-2 sm:flex">
                            <ArrowIcon />
                        </div>
                    </a>
                ))}
            </div>
        </section>
    );
}

function Contact() {
    return (
        <section id="contact" className="mx-auto max-w-5xl px-6 py-20">
            <SectionHeading
                eyebrow="Contact"
                title={
                    <>
                        Say{" "}
                        <span className="font-script font-bold text-accent">
                            hello
                        </span>
                        .
                    </>
                }
            />
            <div className="max-w-2xl">
                <p className="text-lg leading-relaxed text-muted">
                    Want to talk shop, swap project ideas, or just say hi? I
                    always enjoy a good conversation about building things.
                </p>
                <a
                    href="mailto:hello@cheryllane.dev"
                    className="group mt-6 inline-flex items-center gap-2 font-serif text-xl text-fg"
                >
                    hello@cheryllane.dev <ArrowIcon />
                </a>
                <div className="mt-6 flex items-center gap-5">
                    {SOCIALS.map((s) => (
                        <a
                            key={s.label}
                            href={s.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={s.label}
                            className="text-muted transition-colors hover:text-accent"
                        >
                            {s.icon}
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer className="border-t border-line">
            <div className="mx-auto flex max-w-5xl flex-col items-start justify-between gap-4 px-6 py-8 text-sm text-muted sm:flex-row sm:items-center">
                <p>
                    &copy; {new Date().getFullYear()} Cheryl Lane. All rights
                    reserved.
                </p>
                <div className="flex items-center gap-5">
                    {SOCIALS.map((s) => (
                        <a
                            key={s.label}
                            href={s.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={s.label}
                            className="transition-colors hover:text-accent"
                        >
                            {s.icon}
                        </a>
                    ))}
                </div>
            </div>
        </footer>
    );
}

export function App() {
    return (
        <>
            <MenuBar />
            <main>
                <Hero />
                <About />
            </main>
            <Footer />
        </>
    );
}

export default App;
